/**
 * Pure CIDR helpers — used to build the network-prefix tree shown on the
 * Networks page. Kept dependency-free so they remain trivially testable.
 *
 * A CIDR is parsed into:
 *   - `family`: 4 (IPv4) or 6 (IPv6)
 *   - `network`: the network address as a `bigint` (zero-padded to the family
 *     width, so two parsed prefixes from the same family are directly
 *     comparable)
 *   - `mask`: the prefix length in bits (e.g. 24 for `10.0.0.0/24`)
 *
 * Helpers exported:
 *   - `parseCidr(cidr)`  — string → ParsedCidr (throws on malformed input)
 *   - `cidrContains(p, c)` — does `p` strictly-or-equally contain `c`?
 *   - `compareCidr(a, b)`  — sort-friendly comparator
 *     (family asc, network asc, mask asc)
 */

export interface ParsedCidr {
  family: 4 | 6;
  /** Network address (zero-bits below the mask), as a bigint. */
  network: bigint;
  /** Prefix length in bits. */
  mask: number;
}

const IPV4_BITS = 32;
const IPV6_BITS = 128;

/**
 * Expand `::` zero-compression in an IPv6 string into 8 hextets and return
 * them as a fixed-length array of normalised hex strings. Returns `null` if
 * the address is malformed.
 */
function expandIpv6(addr: string): string[] | null {
  // Handle the all-zero edge cases first
  if (addr === '::') {
    return ['0', '0', '0', '0', '0', '0', '0', '0'];
  }

  const doubleColonCount = (addr.match(/::/g) ?? []).length;
  if (doubleColonCount > 1) {
    return null;
  }

  let leftRaw: string;
  let rightRaw: string;
  if (doubleColonCount === 1) {
    const parts = addr.split('::');
    leftRaw = parts[0] ?? '';
    rightRaw = parts[1] ?? '';
  } else {
    leftRaw = addr;
    rightRaw = '';
  }

  const left = leftRaw === '' ? [] : leftRaw.split(':');
  const right = rightRaw === '' ? [] : rightRaw.split(':');

  if (left.length + right.length > 8) {
    return null;
  }
  if (doubleColonCount === 0 && left.length !== 8) {
    return null;
  }

  const fillCount = 8 - (left.length + right.length);
  const middle: string[] = new Array(fillCount).fill('0');
  const hextets = [...left, ...middle, ...right];

  for (const h of hextets) {
    if (h === '' || h.length > 4 || !/^[0-9a-fA-F]+$/.test(h)) {
      return null;
    }
  }

  return hextets;
}

const ZERO = BigInt(0);
const EIGHT = BigInt(8);
const SIXTEEN = BigInt(16);

function ipv4ToBigInt(addr: string): bigint | null {
  const parts = addr.split('.');
  if (parts.length !== 4) {
    return null;
  }
  let acc = ZERO;
  for (const p of parts) {
    if (p === '' || !/^\d+$/.test(p)) {
      return null;
    }
    const n = Number(p);
    if (!Number.isInteger(n) || n < 0 || n > 255) {
      return null;
    }
    acc = (acc << EIGHT) | BigInt(n);
  }
  return acc;
}

function ipv6ToBigInt(addr: string): bigint | null {
  const hextets = expandIpv6(addr);
  if (hextets === null) {
    return null;
  }
  let acc = ZERO;
  for (const h of hextets) {
    acc = (acc << SIXTEEN) | BigInt(parseInt(h, 16));
  }
  return acc;
}

/**
 * Parse a CIDR string (`"10.0.0.0/24"` or `"2001:db8::/32"`) into its parts.
 * Throws on malformed input — callers can catch and treat as "unparseable".
 */
export function parseCidr(cidr: string): ParsedCidr {
  const slash = cidr.indexOf('/');
  if (slash === -1) {
    throw new Error(`Invalid CIDR (missing '/'): ${cidr}`);
  }
  const addr = cidr.slice(0, slash);
  const maskRaw = cidr.slice(slash + 1);
  if (maskRaw === '' || !/^\d+$/.test(maskRaw)) {
    throw new Error(`Invalid CIDR mask: ${cidr}`);
  }
  const mask = Number(maskRaw);

  const family: 4 | 6 = addr.includes(':') ? 6 : 4;
  const totalBits = family === 4 ? IPV4_BITS : IPV6_BITS;

  if (!Number.isInteger(mask) || mask < 0 || mask > totalBits) {
    throw new Error(`Invalid CIDR mask: ${cidr}`);
  }

  const raw = family === 4 ? ipv4ToBigInt(addr) : ipv6ToBigInt(addr);
  if (raw === null) {
    throw new Error(`Invalid CIDR address: ${cidr}`);
  }

  // Apply the mask so the network address has all host bits zeroed — this
  // makes containment checks a simple bitwise compare regardless of whether
  // the caller passed `10.0.0.5/24` or `10.0.0.0/24`.
  const hostBits = totalBits - mask;
  const network = hostBits === 0 ? raw : (raw >> BigInt(hostBits)) << BigInt(hostBits);

  return { family, network, mask };
}

/**
 * Does `parent` contain `child`? Both must be already-parsed. Same-CIDR
 * counts as containment (a prefix contains itself), which is what the tree
 * builder wants when checking "is the row at depth d-1 my parent".
 */
export function cidrContains(parent: ParsedCidr, child: ParsedCidr): boolean {
  if (parent.family !== child.family) {
    return false;
  }
  if (parent.mask > child.mask) {
    return false;
  }
  const totalBits = parent.family === 4 ? IPV4_BITS : IPV6_BITS;
  const hostBits = totalBits - parent.mask;
  if (hostBits === 0) {
    return child.network === parent.network;
  }
  const shifted = child.network >> BigInt(hostBits);
  const parentShifted = parent.network >> BigInt(hostBits);
  return shifted === parentShifted;
}

/**
 * Sort comparator producing canonical tree order:
 *   family asc → network asc → mask asc
 * (a `/16` sorts before its `/24` children, which is what the tree builder
 * walks forward over).
 */
export function compareCidr(a: ParsedCidr, b: ParsedCidr): number {
  if (a.family !== b.family) {
    return a.family - b.family;
  }
  if (a.network < b.network) {
    return -1;
  }
  if (a.network > b.network) {
    return 1;
  }
  return a.mask - b.mask;
}
